<!--
  (c) Copyright 2024 Calabrio, Inc.
  All Rights Reserved. www.calabrio.com LICENSED MATERIALS
  Property of Calabrio, Inc., Minnesota, USA

  No part of this publication may be reproduced, stored or transmitted,
  in any form or by any means (electronic, mechanical, photocopying,
  recording or otherwise) without prior written permission from Calabrio, Inc.
-->

<template>
  <div class="wy-analytics-contact-reasons-detail-metric-analysis-table" v-loading="loading">
    <div class="table-header">
      <span>{{ breakdownHeaderText }}</span>
    </div>
    <div v-if="tableDataExists" class="table-subheader">
      <div class="title">{{ breakdownSubheaderText }}</div>
      <div class="sub-section">
        <div class="value" :class="valueColorClass">
          {{ getScore }}<span v-if="getScore != 'N/A'">{{ activeSubPage === 'experience' ? '' : '%' }}</span>
        </div>
        <div class="sub-section">
          <div class="target-icon"></div>
          <div>
            {{ getTarget }}<span v-if="getTarget != 'N/A'">{{ activeSubPage === 'experience' ? '' : '%' }}</span>
          </div>
        </div>
      </div>
    </div>
    <el-table v-if="getBesOrBasData.length > 0" :data="getBesOrBasData" ref="metricAnalysisTable" :border="true" class="metric-analysis-table" :default-sort="{ prop: 'impact', order: 'descending' }">
      <el-table-column prop="name" :label="getTableTitle" width="450">
        <template slot-scope="{ row, $index }">
          <div class="circle-indicator-row">
            <span class="indicator-signal">{{ formatName(row, $index) }}</span>
            <div :style="{ backgroundColor: getRowColor(row.name), height: '8px', width: '8px', borderRadius: '50%' }"></div>
            <div v-if="row.name == 'Bad Containment'">
              <el-tooltip effect="dark" popper-class="wy-page-tooltip-popup" placement="bottom">
                <div slot="content"><span v-html="badContainmentTooltipContent"></span></div>
                <img class="bad-con-info-icon" src="../../assets/icons/icon_info.svg" />
              </el-tooltip>
            </div>
          </div>
        </template>
      </el-table-column>
      <el-table-column prop="total" sortable label="# Conversations" class-name="col-center">
        <template slot-scope="{ row }">
          {{ formatNumber(row.total) }}
        </template>
      </el-table-column>
      <el-table-column prop="totalPercent" sortable label="% Conversations" class-name="col-center">
        <template slot-scope="{ row }">
          {{ formatPercentage(row.totalPercent) }}
        </template>
      </el-table-column>

      <el-table-column sortable prop="impact" class-name="col-center">
        <template #header>
          <div class="info-wrapper">
            <span>Impact</span>
            <el-tooltip effect="dark" popper-class="wy-page-tooltip-popup" placement="bottom">
              <div slot="content"><span v-html="activeSubPage === 'experience' ? besTooltipContent : basTooltipContent"></span></div>
              <img class="info-icon" src="../../assets/icons/icon_info.svg" />
            </el-tooltip>
          </div>
        </template>
        <template slot-scope="{ row }">
          {{ formatDecimal(row.impact) }}
        </template>
      </el-table-column>
    </el-table>
    <div v-else class="no-data">No data available</div>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';

export default Vue.extend({
  name: 'wy-analytics-contact-reasons-detail-metric-analysis-table',
  props: ['loading', 'parent', 'activeSubPage', 'tableData', 'topicDisplayName'],

  data() {
    return {
      besTooltipContent:
        'The impact metric measures the degree to which each signal contributes to the reduction of BES. The total impact is, thus, the cumulative effect of all signals, i.e.BES = 100 - Total impact.',
      basTooltipContent:
        'The impact metric measures the degree to which each signal contributes to the reduction of BAS. The total impact is, thus, the cumulative effect of all signals, i.e.BAS = 100 - Total impact.',

      badContainmentTooltipContent: 'Conversations which are not escalated, but cover a topic that the bot is known to be ineffective at automating, making the ‘containment’ unsatisfactory.',
    };
  },

  computed: {
    ...mapGetters(['companyProfileSettings']),

    tableDataExists() {
      return this.tableData && (this.tableData.bes || this.tableData.bas);
    },
    getBesOrBasData() {
      if (!this.tableDataExists) {
        return [];
      }
      const data = this.activeSubPage === 'experience' ? this.tableData.bes.data : this.tableData.bas.data;

      if (data) {
        return data.filter((element) => element.impact !== 0 && element.impact !== -1);
      } else {
        return [];
      }
    },
    // used in analysis-transcripts
    getAllBesAndBasSignals() {
      const signals = {
        bes: [],
        bas: [],
      };

      if (!this.tableDataExists) {
        return signals;
      }

      if (this.tableData.bes.data) {
        this.tableData.bes.data.forEach((element) => {
          if (element.impact !== 0) {
            signals.bes.push(element);
          }
        });
      }

      if (this.tableData.bas.data) {
        this.tableData.bas.data.forEach((element) => {
          if (element.impact !== 0) {
            signals.bas.push(element);
          }
        });
      }

      signals.bes = signals.bes.map((signal) => signal.name);
      signals.bas = signals.bas.map((signal) => signal.name);

      return signals;
    },

    breakdownHeaderText() {
      return this.activeSubPage === 'experience' ? 'Topic CX Breakdown' : 'Topic Automation Breakdown';
    },
    breakdownSubheaderText() {
      return this.activeSubPage === 'experience' ? 'BES' : 'BAS';
    },
    valueColorClass() {
      if (this.getScore === 'N/A') return 'na-color';

      if (this.getScore < this.getTarget) {
        return 'red-color';
      }
      return 'green-color';
    },
    getScore() {
      if (!this.tableDataExists) {
        return 'N/A';
      }
      const data = this.activeSubPage === 'experience' ? this.tableData.bes && this.tableData.bes.score : this.tableData.bas && this.tableData.bas.score;
      if (typeof data === 'number' && data >= 0) return data;

      return 'N/A';
    },
    getTarget() {
      if (!this.tableDataExists) {
        return 'N/A';
      }

      if (
        this.companyProfileSettings &&
        this.companyProfileSettings.targetSettings &&
        this.companyProfileSettings.targetSettings.settings &&
        this.companyProfileSettings.targetSettings.settings.home
      ) {
        const settings = this.companyProfileSettings.targetSettings.settings.home.experienceAutomationSummary;

        const topicLevelTarget = settings.contactReasons && settings.contactReasons.length && settings.contactReasons.find((topic) => topic.name === this.topicDisplayName);
        const defaultTarget = settings.default;

        if (topicLevelTarget && topicLevelTarget.actived) {
          let target = null;
          let sectionName = this.activeSubPage === 'experience' ? "bes": "bas";
          target = (topicLevelTarget.target[`${sectionName}`] && topicLevelTarget.target[`${sectionName}`].value) ? topicLevelTarget.target[`${sectionName}`].value : defaultTarget.target[`${sectionName}`].maxValue;
          return typeof target === 'number' && target >= 0 ? target : 'N/A';
        } else if (defaultTarget && defaultTarget.actived) {
          if ((defaultTarget.target.bes && defaultTarget.target.bes.maxValue) || (defaultTarget.target.bas && defaultTarget.target.bas.maxValue)) {
            const target = this.activeSubPage === 'experience' ? defaultTarget.target.bes.maxValue : defaultTarget.target.bas.maxValue;
            return typeof target === 'number' && target >= 0 ? target : 'N/A';
          } else if ((defaultTarget.target.bes && defaultTarget.target.bes.value) || (defaultTarget.target.bas && defaultTarget.target.bas.value)) {
            const target = this.activeSubPage === 'experience' ? defaultTarget.target.bes.value : defaultTarget.target.bas.value;
            return typeof target === 'number' && target >= 0 ? target : 'N/A';
          }
        } else {
          return 'N/A';
        }
      }

      return 'N/A';
    },
    getTableTitle() {
      return this.activeSubPage === 'experience' ? 'Signals causing Experience Gaps' : 'Signals causing Automation Gaps';
    },
  },
  methods: {
    formatName(value, index) {
      // const index = this.getBesOrBasData.indexOf(value);
      return `${Number(index) + 1} \u00A0\u00A0\u00A0\u00A0\u00A0 ${value.name}`;
    },

    formatNumber(value) {
      return value.toLocaleString();
    },

    formatPercentage(value) {
      if (value > 0 && value < 0.1) {
        return '<0.10%';
      }

      return value.toFixed(2) + '%';
    },

    formatDecimal(value) {
      if (value > 0 && value < 0.1) {
        return '<0.1';
      }

      return value.toFixed(1);
    },

    getRowColor(value) {
      const colors = this.parent.breakdownColorScheme;
      return colors[value];
    },
  },
});
</script>

<style lang="scss">
.wy-analytics-contact-reasons-detail-metric-analysis-table {
  font-family: Helvetica Neue, Helvetica, Arial, sans-serif !important;

  .table-header {
    display: flex;
    align-items: center;

    border: 1px solid #ebeef5;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    border-bottom: 0px;

    padding: 14px 22px;
    font-weight: 500;
    font-size: 18px;

    border-bottom: 1px solid #ebeef5;
    cursor: default;
  }

  .table-subheader {
    border-left: 1px solid #ebeef5;
    border-right: 1px solid #ebeef5;
    padding: 20px 22px;

    .title {
      font-weight: 500;
      margin-bottom: 8px;
      cursor: default;
    }
    .sub-section {
      display: flex;
      align-items: center;
      gap: 8px;

      .value {
        font-size: 26px;
      }
      .red-color {
        color: #c11149;
      }
      .green-color {
        color: #186731;
      }
      .na-color {
        color: #4b5158;
      }

      .target-icon {
        background-image: url('../../assets/icons/icon_besbas_target.svg');
        background-size: 80%;
        background-repeat: no-repeat;
        padding: 10px;
        background-position: center;
        margin-right: -4px;
      }
    }
  }

  .circle-indicator {
    height: 16px;
    width: 16px;
    border-radius: 50%;
  }

  .metric-analysis-table {
    background: #ffffff 0% 0% no-repeat padding-box;
    box-shadow: 0px 1px 2px #0000000f;
    width: 100%;
    opacity: 1;
    font-size: 12px;

    .circle-indicator-row {
      display: flex;
      align-items: center;
      gap: 4px;
      line-height: 17px !important;
      height: 17px !important;
      // put regular cursor
      cursor: default;

      .bad-con-info-icon {
        height: 16px;
        margin-top: 6px;
        margin-left: 4px;
        cursor: pointer;
      }
    }

    .el-table__body-wrapper {
      height: 100%;
    }

    th,
    td {
      color: var(--site-font-tertiary-color);
      border: 0px;
      font-size: 14.22px;
    }

    th {
      background-color: #fafbfb;
      border-bottom: 1px solid #ebeef5;
      padding: 8px 0px;
      &.col-center {
        text-align: center !important;
      }
    }

    th > .cell {
      min-width: 148px;
    }

    th:nth-child(1) > .cell {
      padding-left: 24px;
      line-height: 17px !important;
    }

    // Impact column to allow sortable
    th:nth-last-child(1) > .cell,
    th:nth-last-child(2) > .cell {
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
    }

    td {
      border-bottom: 1px solid #ebeef5;
      padding: 14px 14px;
      cursor: default;
      &.col-center {
        text-align: center !important;
        padding-right: 28px;
      }
    }

    .info-wrapper {
      display: flex;
      align-items: center;
      justify-content: center;

      .info-icon {
        height: 16px;
        margin-top: 2px;
        margin-left: 4px;
        margin-right: -3px;
        cursor: pointer;
      }
    }
  }

  .no-data {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    font-size: 14px;
    color: gray;

    padding: 20px;
    border: 1px solid #ebeef5;
    border-top: none;
  }
}
</style>
